<script lang="ts" context="module">
  import type { Load } from '@sveltejs/kit';
  import { getUserStore } from '$lib/stores/getUserStore';
  import type { UserSkillStore } from 'shared/src/user/userKnowledge/user.skill.store';
  import { uniqueFilter } from 'shared/src/utils/uniqueFilter';
  import type { ClientUserStore } from 'shared/src';
  import { getRecommendedAndOptionalPracticesForSkills } from '$lib/app/skills/getRecommendedAndOptionalPracticesForSkills';

  export const load: Load = async ({ session, url, fetch }) => {
    const clientUserStore = await getUserStore({ userWithJoins: session.user, fetch });

    const topicQueryParam = url.searchParams.get('topic');
    const skillsQueryParam = url.searchParams.getAll('skills');

    const pageTitle = getPageTitleFromSkillAndTopicIds({
      topicId: topicQueryParam,
      skillIds: skillsQueryParam,
      clientUserStore,
    });

    let topicSkillIds = topicQueryParam
      ? clientUserStore.knowledgeProgress.topicsById[topicQueryParam]?.skills
          .filter((s) => s.isVisible)
          .map((s) => s.textId) || []
      : [];

    let skillIds = skillsQueryParam
      ? skillsQueryParam
          .map((skillId) => clientUserStore.knowledgeProgress.skillsById[skillId])
          .filter((s): s is UserSkillStore => !!s)
          .filter((s) => s.isVisible)
          .map((s) => s.textId)
      : [];

    let skillIdsToTrain = [...topicSkillIds, ...skillIds].filter(uniqueFilter);

    // If we have nothing to train, go to the main skills page:
    if (!skillIdsToTrain.length) {
      return {
        status: 303,
        redirect: '/app/skills',
      };
    }

    // If we have a topicQueryParam, and all skillIds are within that topic
    // then we should display topic skill stats to student as well
    const allSkillsAreSameTopic = skillIdsToTrain
      .map((skillId) => clientUserStore.knowledgeProgress.skillsById[skillId]?.topic?.textId)
      .every((item, i, arr) => (i === 0 && arr.length ? true : item === arr[i - 1]));

    const topicStore = allSkillsAreSameTopic
      ? clientUserStore.knowledgeProgress.skillsById[skillIdsToTrain[0]!]?.topic
      : undefined;

    // All is well, let's show them the page:
    return {
      props: {
        skillIdsToTrain,
        clientUserStore,
        topicStore,
        pageTitle,
      },
    };
  };
</script>

<script lang="ts">
  import Button from '$lib/components/Button.svelte';
  import H from '$lib/components/H.svelte';
  import { goto } from '$app/navigation';
  import type { SkillsPracticeExercisesQueryParam } from '$lib/app/skills/skillsPracticeExercisesQueryParamDefinition';
  import { getPageTitleFromSkillAndTopicIds } from '$lib/app/skills/getPageTitleFromSkillAndTopicIds';
  import PageTitle from '$lib/teach/resources/PageTitle.svelte';
  import { page } from '$app/stores';
  import { fade } from 'svelte/transition';
  import { browser } from '$app/env';
  import type { UserTopicStore } from 'shared/src/user/userKnowledge/user.topic.store';
  import SkillDetailGraph from '$lib/app/skills/skillDetailGraph/SkillDetailGraph.svelte';

  export let skillIdsToTrain: string[];
  export let clientUserStore: ClientUserStore;
  export let pageTitle: string;
  export let topicStore: UserTopicStore | undefined;

  const { otherPractices, recommendedPractices } = getRecommendedAndOptionalPracticesForSkills(
    skillIdsToTrain,
    clientUserStore
  );

  const needTwoHeaders = recommendedPractices.length && otherPractices.length;

  function startPractice() {
    const practices: SkillsPracticeExercisesQueryParam = [
      ...otherPractices,
      ...recommendedPractices,
    ]
      .filter((p) => p.include)
      .map((practice) => ({ name: practice.name, options: practice.options }));
    const search = $page.url.search + `&exercises=${encodeURIComponent(JSON.stringify(practices))}`;
    if (browser) {
      goto(`/app/skills/practice${search}`);
    }
  }

  const onlyOnePracticeExerciseAvailable =
    [...otherPractices, ...recommendedPractices].length === 1;

  if (onlyOnePracticeExerciseAvailable) {
    [...otherPractices, ...recommendedPractices].forEach((practice) => {
      practice.include = true;
    });
    // We can only goto on the browser, and this code is triggered
    // automatically if there's only one exercise:
    // if (browser) {
    //   startPractice();
    // }
  }
</script>

<PageTitle
  title="{pageTitle} Practice"
  description="Practice exercises for {pageTitle}."
  hideTitle
/>
<div class="card m-4 p-2 text-center" in:fade={{ duration: 250 }}>
  <H>{pageTitle} Practice</H>
  <div
    class="text-center {needTwoHeaders
      ? 'md:text-left'
      : ''} md:flex md:justify-around md:space-x-2"
  >
    {#if onlyOnePracticeExerciseAvailable}
      <div class="m-2">
        {#each [...recommendedPractices, ...otherPractices] as practiceExercise}
          {practiceExercise.description}<br />
        {/each}
      </div>
    {:else}
      {#if recommendedPractices.length}
        <div class="m-2">
          {#if !needTwoHeaders}
            <H level={2}>Exercises:</H>
          {:else}
            <H level={2}>Recommended Exercises:</H>
          {/if}
          {#each recommendedPractices as practiceExercise}
            <label>
              <input type="checkbox" bind:checked={practiceExercise.include} />
              {practiceExercise.description}<br />
            </label>
          {/each}
        </div>
      {/if}
      {#if otherPractices.length}
        <div class="m-2">
          {#if !needTwoHeaders}
            <H level={2}>Exercises:</H>
          {:else}
            <H level={2} class="mt-4 md:mt-0">Other Exercises:</H>
          {/if}
          {#each otherPractices as practiceExercise}
            <label>
              <input type="checkbox" bind:checked={practiceExercise.include} />
              {practiceExercise.description}<br />
            </label>
          {/each}
        </div>
      {/if}
    {/if}
  </div>
  <Button on:click={startPractice} class="my-4">Start Practice</Button>
</div>

{#if topicStore}
  <div class="card m-4 p-2" in:fade={{ duration: 250 }}>
    <H class="text-center">{pageTitle} Stats</H>
    <div class="text-xs">
      <SkillDetailGraph topic={topicStore} />
    </div>
  </div>
{/if}

<style lang="postcss">
  input {
    @apply mt-2;
  }

  :global(h2) {
    @apply mb-1;
  }
</style>
